// src/utils/saveChangesUtils.js
// Build the "what will this save change" delta shown in the Save confirmation
// dialog. The delta compares the deal as originally loaded from Snowflake
// (`original`) against the deal about to be written (`current` + the per-tranche
// ownership / purchase-price overrides), and lists every field that differs —
// not just the two price-popup fields.

import dayjs from 'dayjs';

// Label used for deal-level (non per-tranche) field changes.
export const DEAL_LEVEL_TRANCHE = '—'; // em dash

// Structural / metadata / per-tranche / UI-only keys that must not appear as
// deal-level field changes. Per-tranche fields (ownership, purchase price) are
// diffed explicitly below; the rest are identity or bookkeeping columns.
export const SKIP_DEAL_FIELDS = new Set([
  'Deal Name', 'DEAL_NAME',
  'Tranche', 'TRANCHE',
  'CUSIP', 'PRE_CUSIP',
  'INTEX_DEAL', 'Intex Deal', 'INTEX Deal',
  'Modified By', 'MODIFIED_BY',
  'Modified Date', 'MODIFIED_DATE',
  'ASOFDT',
  'Status', 'STATUS',
  // per-tranche fields handled explicitly
  'Ownership Percentage', 'OWN_PCT',
  'PURCHASE_PRICE', 'Purchase Price',
  // UI-only / action / raw-duplicate keys
  'Load', 'Edit', 'Clone',
  'SEC_OWNER',
]);

// Fields that never reach the cashflow engine (column_mapping.json: Security
// Owner "does not affect the model"), so editing only these leaves the stored
// cashflows and analytics valid and the save can skip Generate.
export const NON_CASHFLOW_FIELDS = new Set(['Security Owner']);

// Whether a save made of these changes can go through without a fresh Generate.
export const canSaveWithoutGenerate = (changes) =>
  changes.length > 0 && changes.every((change) => NON_CASHFLOW_FIELDS.has(change.field));

// Keys /save never persists as model inputs: the server stamps Modified By/Date,
// and Load/Edit/Clone are grid actions.
const BOOKKEEPING_FIELDS = new Set([
  'Modified By', 'Modified Date', 'MODIFIED_BY', 'MODIFIED_DATE',
  'Load', 'Edit', 'Clone',
]);

// Normalize a cell value for comparison: null/undefined/blank all collapse to ''
// and everything else compares as a trimmed string, so "20" === 20 and
// null === '' (avoids spurious diffs from JSON round-tripping).
const norm = (v) => (v === null || v === undefined ? '' : String(v).trim());

// A value as /save would persist it. The form joins per-tranche lists with ','
// where /deals joins them with ', ', and tranche_map compares by content.
const canonical = (v) => {
  if (Array.isArray(v)) return v.map(norm).join(',');
  if (v !== null && typeof v === 'object' && !(v instanceof Date)) {
    return JSON.stringify(Object.keys(v).sort().map((k) => [k, v[k]]));
  }
  return norm(v).split(',').map((s) => s.trim()).join(',');
};

// The calendar day of an as-of: /deals sends an HTTP date
// ("Sun, 01 Jun 2025 00:00:00 GMT"), the edit form a local Date.
const asofDay = (v) => {
  if (v instanceof Date) return dayjs(v).format('YYYY-MM-DD');
  const s = norm(v);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? s : parsed.toISOString().slice(0, 10);
};

// Each tranche's CUSIPs, sorted. A tranche_map entry wins (the edit form adds one
// from INTEX's /deal; /deals rows carry none); otherwise a tranche takes the CUSIP
// at its position in the joined lists, or the deal's only CUSIP.
const resolvedTrancheMap = (deal) => {
  const cusips = splitAligned(deal.CUSIP);
  const map = {};
  splitAligned(deal.Tranche ?? deal.TRANCHE).forEach((tranche, i) => {
    if (!tranche) return;
    const mapped = [deal.tranche_map?.[tranche]].flat().map(norm).filter(Boolean);
    const aligned = cusips[i] || (cusips.length === 1 ? cusips[0] : '');
    map[tranche] = [...new Set([...(map[tranche] || []), ...(mapped.length ? mapped : [aligned])])]
      .filter(Boolean)
      .sort();
  });
  return map;
};

/**
 * The deal row to save without a fresh Generate, or null when the save needs one.
 *
 * Every pending change must be a non-cashflow field, and nothing else /save
 * persists (each tranche's CUSIPs, every assumption) may differ from the row as
 * loaded. The edit form resets ASOFDT to today, so today counts as no change;
 * any other date needs a Generate. The row returned is the loaded row with only
 * the non-cashflow fields taken from the edit, so it keeps the as-of of the
 * cashflows already stored, and its stored tranche/CUSIP pairs as the tranche_map
 * so /save writes under them instead of re-resolving through INTEX. A CUSIP
 * shared by several tranches always needs a Generate: /save splits it into
 * INTEX's per-tranche CUSIPs.
 *
 * @param {object}   params.original  Pristine deal as loaded from /deals.
 * @param {object}   params.current   Deal being saved (selectedRows[0]) after any edits.
 * @param {Array}    params.changes   computeSaveChanges output for this save.
 * @param {string}   params.today     Today as 'YYYY-MM-DD' (the form's ASOFDT default).
 * @returns {object|null}
 */
export function dealToSaveWithoutGenerate({
  original,
  current,
  changes,
  today = dayjs().format('YYYY-MM-DD'),
}) {
  if (!original || !current || Object.keys(original).length === 0) return null;
  if (!canSaveWithoutGenerate(changes)) return null;

  const storedTrancheMap = resolvedTrancheMap(original);
  const storedCusips = Object.values(storedTrancheMap).flat();
  if (new Set(storedCusips).size < storedCusips.length) return null;
  if (canonical(resolvedTrancheMap(current)) !== canonical(storedTrancheMap)) return null;

  const keys = new Set([...Object.keys(original), ...Object.keys(current)]);
  const onlyNonCashflowEdits = [...keys].every((key) => {
    if (NON_CASHFLOW_FIELDS.has(key) || BOOKKEEPING_FIELDS.has(key)) return true;
    // Compared above as resolved tranche/CUSIP pairs.
    if (['Tranche', 'TRANCHE', 'CUSIP', 'tranche_map'].includes(key)) return true;
    if (key === 'ASOFDT') {
      return [asofDay(original.ASOFDT), today].includes(asofDay(current.ASOFDT));
    }
    // Lower-case keys are UI-only (security_owner, intexDeal).
    if (/^[a-z]/.test(key)) return true;
    return canonical(original[key]) === canonical(current[key]);
  });
  if (!onlyNonCashflowEdits) return null;

  const deal = { ...original, tranche_map: storedTrancheMap };
  NON_CASHFLOW_FIELDS.forEach((field) => {
    deal[field] = current[field];
  });
  return deal;
}

const isChanged = (a, b) => norm(a) !== norm(b);

// Split a per-tranche comma-joined string (e.g. "A1, A2" / "0.5, 0.3") into a
// position-aligned list. /deals joins Tranche and Ownership Percentage in the
// same order, so index i lines up.
const splitAligned = (joined) =>
  joined === null || joined === undefined
    ? []
    : String(joined).split(',').map((s) => s.trim());

const isBlank = (v) => v === undefined || v === null || v === '';

/**
 * Compute the list of changes between the originally-loaded deal and the deal
 * about to be saved.
 *
 * @param {object}   params.original              Pristine deal as loaded (display-name keyed), or {} for a new deal.
 * @param {object}   params.current               Deal being saved (selectedRows[0]) after any edits.
 * @param {string[]} params.selectedTranches      Tranches included in this save.
 * @param {object}   params.ownPctTrancheTargets  { tranche: ownership% } overrides from the pricing popup.
 * @param {object}   params.priceTrancheTargets   { tranche: purchasePrice } overrides from the pricing popup.
 * @returns {Array<{tranche: string, field: string, oldVal: *, newVal: *}>}
 */
export function computeSaveChanges({
  original = {},
  current = {},
  selectedTranches = [],
  ownPctTrancheTargets = {},
  priceTrancheTargets = {},
} = {}) {
  const orig = original || {};
  const curr = current || {};
  const changes = [];

  // --- Deal-level field changes (assumptions, redemption, reinvestment, ...) ---
  const keys = new Set([...Object.keys(orig), ...Object.keys(curr)]);
  keys.forEach((k) => {
    if (SKIP_DEAL_FIELDS.has(k)) return;
    // Real fields are Title-Case display names or UPPER_SNAKE column keys; skip
    // lowercase/camelCase UI-only keys (tranche_map, security_owner, intexDeal).
    if (/^[a-z]/.test(k)) return;
    if (isChanged(orig[k], curr[k])) {
      changes.push({
        tranche: DEAL_LEVEL_TRANCHE,
        field: k,
        oldVal: orig[k],
        newVal: curr[k],
      });
    }
  });

  // --- Per-tranche ownership % changes ---
  const origTranches = splitAligned(orig.Tranche ?? orig.TRANCHE);
  const origOwn = splitAligned(orig['Ownership Percentage']);
  const origOwnByTranche = {};
  origTranches.forEach((t, i) => {
    origOwnByTranche[t] = origOwn[i] ?? '';
  });

  // PURCHASE_PRICE is a deal-level ('first') value in the loaded row.
  const origPx = orig.PURCHASE_PRICE ?? orig['Purchase Price'];

  selectedTranches.forEach((t) => {
    const newOwn = ownPctTrancheTargets?.[t];
    if (!isBlank(newOwn) && isChanged(origOwnByTranche[t], newOwn)) {
      changes.push({
        tranche: t,
        field: 'Ownership %',
        oldVal: origOwnByTranche[t],
        newVal: newOwn,
      });
    }

    const newPx = priceTrancheTargets?.[t];
    if (!isBlank(newPx) && isChanged(origPx, newPx)) {
      changes.push({
        tranche: t,
        field: 'Purchase Price',
        oldVal: origPx,
        newVal: newPx,
      });
    }
  });

  return changes;
}

export default computeSaveChanges;
